import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  Collapse,
  useMediaQuery,
  Divider,
  Tooltip,
  CircularProgress,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PublicIcon from "@mui/icons-material/Public";
import FilterHdrIcon from '@mui/icons-material/FilterHdr';
import { extractAuthorNames, shouldBracketAuthorship } from "../../../utils/scientificNameFormatter";
import supabase from "../../../utils/supabase";

// ランク優先度 (ベース参照コードのもの)
const RANK_PRIORITY = {
  kingdom: 1, phylum: 2, class: 3, order: 4, superfamily: 5, family: 6,
  subfamily: 7, tribe: 8, genus: 9, subgenus: 10, species: 11,
  "replacement_name": 11, // replacement_name は species と同等扱い
  subspecies: 12,
  synonym: 9999, // synonym/homonym は優先度最低
  homonym: 9999,
  invalid: 9999,
};

// ランク色 (ベース参照コードのもの)
const rankStyles = {
  kingdom: { color: "#FFEB3B" }, phylum: { color: "#FF9800" }, class: { color: "#FF7043" },
  order: { color: "#F06292" }, superfamily: { color: "#E040FB" }, family: { color: "#BA68C8" },
  subfamily: { color: "#CE93D8" }, tribe: { color: "#B39DDB" }, genus: { color: "#4FC3F7" },
  subgenus: { color: "#81D4FA" }, species: { color: "#9CCC65" },
  replacement_name: { color: "#80CBC4" }, // replacement_name の色
  subspecies: { color: "#C5E1A5" },
  synonym: { color: "#A0A0A0" }, // Synonym/Homonym の色を少し調整
  homonym: { color: "#A0A0A0" },
  invalid: { color: "#EF9A9A" },
  default: { color: "aquamarine" },
};

// インデント (ベース参照コードの値)
const INDENT_MULTIPLIER_MOBILE = 1.5;
const INDENT_MULTIPLIER_DESKTOP = 2.5;
const LINE_COLOR = "rgba(151, 235, 234, 0.68)"; // 線の色

export default function TaxonomicTree({
  rootScientificName,
  allScientificNames: allScientificNamesProp = [],
  scientificNameAuthors: scientificNameAuthorsProp = [],
  descendantCountMap = {},
  highlightId,
  onSelectScientificName,
  onFetchChildren,
  fetchedChildren = new Set(),
  loadingChildren = new Set(),
  expandedNodes,
  setExpandedNodes,
  persistExpanded,
  setPersistExpanded,
  childrenTotals = {},
  childPageSize = 50,
}) {
  const { t } = useTranslation(["taxonomy", "common"]);
  const [localExpanded, setLocalExpanded] = useState({});
  const [localPersist, setLocalPersist] = useState(new Set());
  const [auditActorMap, setAuditActorMap] = useState(new Map());
  const fetchedAuditIdsRef = useRef(new Set());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 親から渡されなければローカルステートを使用
  const expanded = expandedNodes ?? localExpanded;
  const setExpanded = setExpandedNodes ?? setLocalExpanded;
  const persist = persistExpanded ?? localPersist;
  const setPersist = setPersistExpanded ?? setLocalPersist;

  const allScientificNames = useMemo(() => (Array.isArray(allScientificNamesProp) ? allScientificNamesProp : []), [allScientificNamesProp]);
  const allScientificNamesMap = useMemo(
    () => new Map(allScientificNames.map((n) => [n.id, n])),
    [allScientificNames],
  );
  const scientificNameAuthors = useMemo(() => (Array.isArray(scientificNameAuthorsProp) ? scientificNameAuthorsProp : []), [scientificNameAuthorsProp]);
  const authorsMap = useMemo(() => {
    const m = new Map();
    scientificNameAuthors.forEach((rel) => {
      const sid = rel.scientific_name_id;
      if (!sid) return;
      const arr = m.get(sid) || [];
      arr.push(rel);
      m.set(sid, arr);
    });
    return m;
  }, [scientificNameAuthors]);

  const isBotActor = useCallback((log) => {
    const actor = String(
      log?.actor_display ||
      log?.actor ||
      log?.actor_id ||
      log?.user_id ||
      ""
    ).toLowerCase();
    if (!actor) return false;
    return actor === "bot" || actor.startsWith("bot:") || actor.includes("[bot]");
  }, []);

  const isBotByAudit = useCallback((id) => {
    if (!id) return false;
    const cached = auditActorMap.get(String(id));
    if (typeof cached === "boolean") return cached;
    return isBotActor(cached);
  }, [auditActorMap, isBotActor]);

  useEffect(() => {
    let cancelled = false;
    const fetchAudit = async () => {
      const ids = (Array.isArray(allScientificNames) ? allScientificNames : []).map((n) => n?.id).filter(Boolean);
      const newIds = ids.filter((id) => !fetchedAuditIdsRef.current.has(id));
      if (!newIds.length) return;

      const chunkSize = 200;
      const updates = [];

      const fetchChunk = async (chunk) => supabase
        .from("audit_log")
        .select("row_id, actor_id, created_at")
        .eq("table_name", "scientific_names")
        .in("row_id", chunk)
        .order("created_at", { ascending: false });

      for (let i = 0; i < newIds.length; i += chunkSize) {
        const chunk = newIds.slice(i, i + chunkSize);
        const res = await fetchChunk(chunk);
        if (res.error) {
          console.warn("[audit_log] fetch failed:", res.error.message);
          continue;
        }
        const seen = new Set();
        (res.data || []).forEach((row) => {
          if (!row?.row_id || seen.has(row.row_id)) return;
          seen.add(row.row_id);
          updates.push({ row_id: row.row_id, isBot: isBotActor(row) });
        });
      }

      if (cancelled || !updates.length) return;
      setAuditActorMap((prev) => {
        const next = new Map(prev);
        updates.forEach(({ row_id, isBot }) => {
          if (!next.has(row_id)) next.set(row_id, isBot);
        });
        return next;
      });
      updates.forEach(({ row_id }) => fetchedAuditIdsRef.current.add(row_id));
    };
    fetchAudit();
    return () => { cancelled = true; };
  }, [allScientificNames, isBotActor]);

  // --- Helper Functions ---
  const getParentChain = useCallback((node) => {
    if (!node?.id) return [];
    const chain = []; let current = node; const visited = new Set([current.id]);
    while (current?.current_parent) {
      const parentId = current.current_parent;
      if (visited.has(parentId)) { console.warn("Circular:", current.id, "->", parentId); break; }
      const parent = allScientificNames.find((n) => n?.id === parentId);
      if (!parent) break;
      chain.unshift(parent); visited.add(parentId); current = parent;
    } return chain;
  }, [allScientificNames]);

  const isInvalidRank = useCallback((rank) => (rank || "").toLowerCase() === "invalid", []);

  const getValidChildren = useCallback((nodeId) => {
    return allScientificNames
      .filter((n) => {
        if (n?.current_parent !== nodeId) return false;
        const rankLower = (n.current_rank || "").toLowerCase();
        if (rankLower === "synonym" || rankLower === "homonym") return false;
        if (isInvalidRank(rankLower)) return false;
        return true;
      })
      .sort((a, b) => {
        const rankA = RANK_PRIORITY[a.current_rank?.toLowerCase()] || 9999;
        const rankB = RANK_PRIORITY[b.current_rank?.toLowerCase()] || 9999;
        if (rankA !== rankB) return rankA - rankB;
        return (a.name_spell_valid || "").localeCompare(b.name_spell_valid || "");
      });
  }, [allScientificNames, isInvalidRank]);

  const getInvalidChildren = useCallback((nodeId) => {
    return allScientificNames
      .filter((n) => n?.current_parent === nodeId && isInvalidRank(n.current_rank))
      .sort((a, b) => (a.name_spell_valid || "").localeCompare(b.name_spell_valid || ""));
  }, [allScientificNames, isInvalidRank]);

  const getSpecialChildren = useCallback((nodeId) => {
    const synonyms = allScientificNames.filter(n => n?.current_parent === nodeId && n.current_rank?.toLowerCase() === "synonym").sort((a,b)=>(a.name_spell_valid||"").localeCompare(b.name_spell_valid||""));
    const homonyms = allScientificNames.filter(n => n?.current_parent === nodeId && n.current_rank?.toLowerCase() === "homonym").sort((a,b)=>(a.name_spell_valid||"").localeCompare(b.name_spell_valid||""));
    return { synonyms, homonyms };
  }, [allScientificNames]);

  const getAuthorsText = useCallback((scientificNameId) => {
    const rels = authorsMap.get(scientificNameId) || [];
    const authors = extractAuthorNames({ scientific_name_and_author: rels });

    if (!authors.length) return "";
    if (authors.length === 1) return authors[0];
    if (authors.length === 2) return `${authors[0]} & ${authors[1]}`;
    return `${authors.slice(0, -1).join(", ")} & ${authors.at(-1)}`;
  }, [authorsMap]);

  const formatAuthority = useCallback((node) => {
    if (!node) return { text: "", autoFilled: false };
    const rels = authorsMap.get(node.id) || [];
    const hasAuthorRel = rels.length > 0;
    const authors = hasAuthorRel ? getAuthorsText(node.id) : "";
    const year = node.authority_year || "";
    const gbifAuth = node.authority_from_gbif || "";
    const base = hasAuthorRel
      ? [authors, year].filter(Boolean).join(", ")
      : gbifAuth
        ? gbifAuth
        : [authors, year].filter(Boolean).join(", ");
    const needsParens = shouldBracketAuthorship(node, allScientificNamesMap);
    const text = needsParens && base ? `(${base})` : base;
    const autoFilled = isBotByAudit(node?.id) || (!hasAuthorRel && !!gbifAuth);
    return { text, autoFilled };
  }, [getAuthorsText, authorsMap, isBotByAudit, allScientificNamesMap]);

  const formatRankLabel = useCallback((rank) => {
    if (!rank) return t("taxonomy:tree.rankNA", { defaultValue: "N/A" });
    return rank.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }, [t]);

  const isItalicRank = useCallback((r) => {
    const rank = (r || "").toLowerCase();
    return rank === "genus" || rank === "subgenus" || rank === "species" || rank === "subspecies" || rank === "replacement_name";
  }, []);

  const hasGbifUsageKey = useCallback((node) => {
    const raw = node?.gbif_usage_key;
    if (raw === null || raw === undefined) return false;
    const str = Array.isArray(raw) ? raw.join(",") : String(raw);
    return str.trim().length > 0;
  }, []);

  const getDescendantCountByRank = useCallback((node, visited = new Set()) => {
      if (!node || !node.id || visited.has(node.id)) return { valid: {}, invalid: {} };
      visited.add(node.id);
      const counts = {};
      const children = getValidChildren(node.id);
      children.forEach((child) => {
          const rank = (child.current_rank || "default").toLowerCase();
          counts[rank] = (counts[rank] || 0) + 1;
          const descendantCounts = getDescendantCountByRank(child, new Set(visited));
          for (const [r, count] of Object.entries(descendantCounts.valid || {})) {
              counts[r] = (counts[r] || 0) + count;
          }
      });
      return { valid: counts, invalid: {} };
  }, [getValidChildren]);

  const formatDescList = useCallback((countsObj) => {
    if (!countsObj || Object.keys(countsObj).length === 0) return [];
    const sortedRanks = Object.keys(countsObj).sort((a, b) => (RANK_PRIORITY[a] || 9999) - (RANK_PRIORITY[b] || 9999));
    return sortedRanks.map((r) => {
      const pluralRank = t(`taxonomy:plurals.${r.replace(/_/g, "")}`, { defaultValue: r + "s" });
      return `${countsObj[r]} ${pluralRank}`;
    });
  }, [t]);

  useEffect(() => {
    if (!highlightId) return;
    const targetNode = allScientificNames.find((n) => n?.id === highlightId);
    if (!targetNode) return;
    const parents = getParentChain(targetNode); const newExpanded = {};
    parents.forEach((p) => { if (p?.id) newExpanded[p.id] = true; });
    newExpanded[targetNode.id] = true;
    setExpanded(prev => ({ ...prev, ...newExpanded }));
  }, [highlightId, allScientificNames, getParentChain, setExpanded]);

  const expandAll = useCallback(() => {
    const allIds = {}; allScientificNames.forEach((node) => { if (node?.id) allIds[node.id] = true; });
    setExpanded(allIds);
    setPersist(new Set(allScientificNames.map((n) => n?.id).filter(Boolean)));
   }, [allScientificNames, setExpanded, setPersist]);
  const collapseAll = useCallback(() => { setExpanded({}); setPersist(new Set()); }, [setExpanded, setPersist]);

  // フェッチ中のノードは強制的に展開状態に保持する（ロード中に閉じてしまうのを防ぐ）
  useEffect(() => {
    if (!loadingChildren || loadingChildren.size === 0) return;
    const toExpand = {};
    loadingChildren.forEach((id) => { toExpand[id] = true; });
    setExpanded((prev) => ({ ...prev, ...toExpand }));
  }, [loadingChildren, setExpanded]);

  // 永続展開セットを expandedNodes に同期（データ更新で再レンダーしても開いたまま維持）
  useEffect(() => {
    if (!persist.size) return;
    const toExpand = {};
    persist.forEach((id) => { toExpand[id] = true; });
    setExpanded((prev) => ({ ...prev, ...toExpand }));
  }, [persist, fetchedChildren, allScientificNames, setExpanded]);

  const handleNodeClick = useCallback((node) => {
    if (!node?.id) return;
    const isCurrentlyExpanded = !!expanded[node.id];
    const willExpand = !isCurrentlyExpanded;

    if (willExpand && typeof onFetchChildren === "function" && !fetchedChildren.has(node.id)) {
      onFetchChildren(node.id, 0);
    }

    setPersist((prev) => {
      const next = new Set(prev);
      if (willExpand) next.add(node.id);
      else next.delete(node.id);
      return next;
    });

    setExpanded(prev => ({
        ...prev,
        [node.id]: willExpand
    }));

    if (willExpand) {
        const parents = getParentChain(node);
        const ancestorsToExpand = {};
        parents.forEach((p) => { if (p?.id) ancestorsToExpand[p.id] = true; });
        setExpanded(prev => ({ ...prev, ...ancestorsToExpand }));
    }
  }, [getParentChain, expanded, fetchedChildren, onFetchChildren, setExpanded, setPersist]);


  const renderNode = useCallback((node, level = 0) => {
    if (!node?.id) return null;

    const validChildren = getValidChildren(node.id);
    const invalidChildren = getInvalidChildren(node.id);
    const totalChildren = childrenTotals[node.id];
    const special = getSpecialChildren(node.id);
    const loadedCount = validChildren.length + invalidChildren.length + special.synonyms.length + special.homonyms.length;
    const alreadyFetchedPages = fetchedChildren.get ? fetchedChildren.get(node.id) : undefined;
    const nextPage = typeof alreadyFetchedPages === "number" ? alreadyFetchedPages + 1 : 0;
    const hasMore = typeof totalChildren === "number" ? loadedCount < totalChildren : false;
    const hasValidChildren = validChildren.length > 0;
    const hasSpecialChildren = special.synonyms.length > 0 || special.homonyms.length > 0;
    const hasInvalidChildren = invalidChildren.length > 0;
    const alreadyFetched = fetchedChildren.has ? fetchedChildren.has(node.id) : false;
    const isLoading = loadingChildren.has(node.id);

    const isExpanded = !!expanded[node.id] || persist.has(node.id);
    const isTarget = node.id === highlightId;
    const rank = (node.current_rank || "default").toLowerCase();
    const style = rankStyles[rank] || rankStyles.default;
    const authorityInfo = formatAuthority(node);
    const authorityText = authorityInfo.text;
    const authorityAuto = authorityInfo.autoFilled;
    const rankLabel = formatRankLabel(node.current_rank);
    const showGbifBadge = hasGbifUsageKey(node);

    const rawCounts =
      descendantCountMap[node.id] ||
      getDescendantCountByRank(node);
    const descendantCounts = rawCounts?.valid || rawCounts?.invalid ? rawCounts : { valid: rawCounts || {}, invalid: {} };
    const totalValid = Object.values(descendantCounts.valid || {}).reduce((sum, v) => sum + (v || 0), 0);
    const totalInvalid = Object.values(descendantCounts.invalid || {}).reduce((sum, v) => sum + (v || 0), 0);
    const totalDescendants = totalValid + totalInvalid;
    const hasDescCounts = descendantCountMap[node.id] !== undefined;
    // 直下の子ランク別件数を把握（ロード済み件数）
    const childRankCounts = {};
    validChildren.forEach((c) => {
      const r = (c.current_rank || "default").toLowerCase();
      childRankCounts[r] = (childRankCounts[r] || 0) + 1;
    });
    // childrenTotals があれば、そのランクの総件数で上書き（例: Genus 配下の species 総数）
    if (typeof totalChildren === "number") {
      const childRanks = Object.keys(childRankCounts);
      if (childRanks.length === 1) {
        const r = childRanks[0];
        descendantCounts.valid[r] = totalChildren;
      }
    }
    const validList = formatDescList(descendantCountMap[node.id]?.valid || []);
    const invalidList = formatDescList(descendantCountMap[node.id]?.invalid || []);
    const maxRows = Math.max(validList.length, invalidList.length);
    const tooltipContent = descendantCountMap[node.id] && totalDescendants > 0
      ? (
        <Box sx={{ minWidth: 260, maxWidth: 320 }}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: "white", display: "block", mb: 0.5, lineHeight: 1.4 }}
          >
            {t("taxonomy:tree.descendantCounts")}
          </Typography>
          <Box sx={{ display: "flex", mb: 0.5 }}>
            <Typography variant="caption" sx={{ flex: 1, color: "white", fontWeight: 600 }}>{t("common:valid")}</Typography>
            <Typography variant="caption" sx={{ flex: 1, color: "white", fontWeight: 600 }}>{t("common:invalid")}</Typography>
          </Box>
          <Stack spacing={0.3}>
            {Array.from({ length: maxRows || 1 }).map((_, idx) => (
              <Box key={`row-${idx}`} sx={{ display: "flex" }}>
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    color: "white",
                    pr: 1,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.3,
                  }}
                >
                  {validList[idx] || (validList.length ? "" : "0")}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    flex: 1,
                    color: "white",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    lineHeight: 1.3,
                  }}
                >
                  {invalidList[idx] || (invalidList.length ? "" : "0")}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      )
      : "";

    const canExpand =
      hasValidChildren ||
      hasSpecialChildren ||
      hasInvalidChildren ||
      isLoading ||
      hasMore ||
      (!alreadyFetched && (!hasDescCounts || totalDescendants > 0));
    const indentMultiplier = isMobile ? INDENT_MULTIPLIER_MOBILE : INDENT_MULTIPLIER_DESKTOP;

    return (
      <Box sx={{ position: 'relative', ml: level * indentMultiplier }}>
        {level > 0 && (
          <Box sx={{ position: "absolute", top: 0, left: `-${indentMultiplier / 2}rem`, height: "100%", width: "1px", bgcolor: LINE_COLOR }} />
        )}

        <Tooltip title={tooltipContent} disableInteractive enterDelay={300}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ bgcolor: isTarget ? "rgba(127,255,212,0.15)" : "transparent", borderRadius: 1, p: 0.3, my: 0.2, minHeight: '24px' }}>
            <Box sx={{ width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              {isLoading ? (
                <CircularProgress size={14} thickness={4} />
              ) : canExpand && (isExpanded ? <ExpandMoreIcon fontSize="small" sx={{ color: '#B0BEC5' }} /> : <ChevronRightIcon fontSize="small" sx={{ color: '#B0BEC5' }} />)}
            </Box>

            <Box
              flexGrow={1}
              onClick={() => canExpand && handleNodeClick(node)}
              sx={{
                cursor: canExpand ? "pointer" : "default",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexWrap: "nowrap",
                minWidth: 0,
              }}
            >
              <Chip
                size="small"
                label={rankLabel}
                variant="outlined"
                sx={{
                  height: 22,
                  borderColor: style.color,
                  color: style.color,
                  fontWeight: 600,
                  "& .MuiChip-label": { px: 0.7, fontSize: "0.72rem", fontWeight: 600, letterSpacing: 0.2 },
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontStyle: isItalicRank(rank) ? "italic" : "normal",
                  fontWeight: isTarget ? 700 : 500,
                  whiteSpace: "nowrap",
                  color: style.color,
                  opacity: isInvalidRank(rank) ? 0.7 : 1,
                  ml: 0.5,
                }}
              >
                {node.name_spell_valid || t("taxonomy:tree.nameNA")}
              </Typography>
              {authorityText && (
                <Stack component="span" direction="row" alignItems="center" spacing={0.5} sx={{ color: "lightgray", whiteSpace: "nowrap", minWidth: 0 }}>
                  <Typography
                    component="span"
                    variant="caption"
                    sx={{ color: "lightgray", whiteSpace: "nowrap" }}
                  >
                    {authorityText}
                  </Typography>
                  {authorityAuto && (
                    <Tooltip title={t("taxonomy:tree.botTooltip")}>
                      <Chip
                        size="small"
                        color="default"
                        icon={<SmartToyIcon sx={{ fontSize: "0.9rem" }} />}
                        label={t("taxonomy:tree.botLabel")}
                        sx={{ height: 18, "& .MuiChip-label": { px: 0.6, fontSize: "0.65rem" }, "& .MuiChip-icon": { fontSize: "1rem" } }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              )}
              {showGbifBadge && (
                <Chip
                  size="small"
                  icon={<PublicIcon sx={{ fontSize: "1rem" }} />}
                  label="GBIF"
                  variant="outlined"
                  sx={{
                    height: 22,
                    borderRadius: 12,
                    color: "#7CFFB0",
                    border: "1px solid rgba(124,255,176,0.6)",
                    backgroundColor: "rgba(124,255,176,0.08)",
                    "& .MuiChip-label": { px: 0.6, fontSize: "0.72rem", fontWeight: 600 },
                    "& .MuiChip-icon": { color: "#7CFFB0", fontSize: "1rem", ml: 0.2 },
                  }}
                />
              )}
              {node.extant_fossil === 'fossil' && (
                <Chip
                  icon={<FilterHdrIcon />}
                  label={t('taxonomy:labels.fossil', 'Fossil')}
                  size="small"
                  sx={{
                    ml: 0.5,
                    height: 20,
                    backgroundColor: '#8B4513', // SaddleBrown
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    '& .MuiChip-icon': { color: 'white', ml: '5px' },
                  }}
                />
              )}

              {onSelectScientificName && (
                <Tooltip title={t("taxonomy:tree.openDetails")} arrow>
                  <Box
                    component="span"
                    sx={{ ml: 1, display: "inline-flex", alignItems: "center", flexShrink: 0 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Chip
                      clickable
                      onClick={() => onSelectScientificName(node)}
                      icon={<InfoOutlinedIcon sx={{ fontSize: "1rem" }} />}
                      label={t("common:open")}
                      sx={{
                        height: 22,
                        borderRadius: 12,
                        color: "aquamarine",
                        border: "1px solid rgba(127,255,212,0.5)",
                        backgroundColor: "rgba(127,255,212,0.08)",
                        "& .MuiChip-label": { px: 0.8, fontSize: "0.7rem", letterSpacing: 0.3, fontWeight: 700 },
                        "& .MuiChip-icon": { color: "aquamarine", fontSize: "1.1rem", ml: 0.2 },
                        "&:hover": { backgroundColor: "rgba(127,255,212,0.15)" },
                      }}
                    />
                  </Box>
                </Tooltip>
              )}
          </Box>
        </Stack>
        </Tooltip>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {hasSpecialChildren && (
            <Box sx={{ ml: (level + 1) * indentMultiplier, pl: 1, mt: 0.5, mb: 0.5, borderLeft: `2px solid ${LINE_COLOR}`, py: 0.5 }}>
              {special.synonyms.map((syn) => {
              const isSynTarget = syn.id === highlightId;
                  const synAuthority = formatAuthority(syn);
                   return ( <Stack key={syn.id} direction="row" alignItems="center" spacing={1} sx={{ bgcolor: isSynTarget ? "rgba(127,255,212,0.1)" : "transparent", borderRadius: 1, p: 0.5, mb: 0.5, flexWrap: "nowrap" }}>
                      <Typography variant="body2" sx={{ color: rankStyles.synonym.color, mr: 0.5, flexShrink: 0 }}>{t("taxonomy:labels.synonym")}:</Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexGrow: 1, flexWrap: "nowrap", minWidth: 0 }}>
                        <Typography variant="body2" sx={{ color: rankStyles.synonym.color, fontStyle: 'italic', whiteSpace: "nowrap" }}>
                          {syn.name_spell_valid}
                        </Typography>
                        {synAuthority.text && (
                          <Stack component="span" direction="row" spacing={0.5} alignItems="center" sx={{ color: "lightgray", whiteSpace: "nowrap" }}>
                            <Typography component="span" variant="caption" sx={{ color: "lightgray" }}>{synAuthority.text}</Typography>
                            {synAuthority.autoFilled && (
                              <Tooltip title={t("taxonomy:tree.botTooltip")}>
                                <Chip
                                  size="small"
                                  color="default"
                                  icon={<SmartToyIcon sx={{ fontSize: "0.9rem" }} />}
                                  label={t("taxonomy:tree.botLabel")}
                                  sx={{ height: 18, "& .MuiChip-label": { px: 0.6, fontSize: "0.65rem" }, "& .MuiChip-icon": { fontSize: "1rem" } }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                        )}
                      </Stack>
                      {onSelectScientificName && (<Tooltip title={t("taxonomy:tree.viewSynonymDetails")} arrow>
                          <Chip
                            clickable
                            onClick={(e) => { e.stopPropagation(); onSelectScientificName(syn); }}
                            icon={<InfoOutlinedIcon fontSize="small" />}
                            label={t("common:open")}
                            sx={{
                              height: 22,
                              borderRadius: 12,
                              color: "aquamarine",
                              border: "1px solid rgba(127,255,212,0.5)",
                              backgroundColor: "rgba(127,255,212,0.08)",
                              ml: "auto",
                              flexShrink: 0,
                              "& .MuiChip-label": { px: 0.8, fontSize: "0.7rem", letterSpacing: 0.3, fontWeight: 700 },
                              "& .MuiChip-icon": { color: "aquamarine", fontSize: "1rem", ml: 0.2 },
                              "&:hover": { backgroundColor: "rgba(127,255,212,0.15)" },
                            }}
                          />
                        </Tooltip> )}
                    </Stack> ); })}
               {special.homonyms.map((hm) => {
                  const isHmTarget = hm.id === highlightId;
                   const hmAuthority = formatAuthority(hm);
                    return ( <Stack key={hm.id} direction="row" alignItems="center" spacing={1} sx={{ bgcolor: isHmTarget ? "rgba(127,255,212,0.1)" : "transparent", borderRadius: 1, p: 0.5, mb: 0.5, mt: special.synonyms.length > 0 ? 0.5 : 0, flexWrap: "nowrap" }}>
                        <Typography variant="body2" sx={{ color: rankStyles.homonym.color, mr: 0.5, flexShrink: 0 }}>{t("taxonomy:labels.homonym")}:</Typography>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexGrow: 1, flexWrap: "nowrap", minWidth: 0 }}>
                          <Typography variant="body2" sx={{ color: rankStyles.homonym.color, fontStyle: 'italic', whiteSpace: "nowrap" }}>
                           {hm.name_spell_valid}
                          </Typography>
                          {hmAuthority.text && (
                            <Stack component="span" direction="row" spacing={0.5} alignItems="center" sx={{ color: "lightgray", whiteSpace: "nowrap" }}>
                              <Typography component="span" variant="caption" sx={{ color: "lightgray" }}>{hmAuthority.text}</Typography>
                              {hmAuthority.autoFilled && (
                                <Tooltip title={t("taxonomy:tree.botTooltip")}>
                                  <Chip
                                    size="small"
                                    color="default"
                                    icon={<SmartToyIcon sx={{ fontSize: "0.9rem" }} />}
                                    label={t("taxonomy:tree.botLabel")}
                                    sx={{ height: 18, "& .MuiChip-label": { px: 0.6, fontSize: "0.65rem" }, "& .MuiChip-icon": { fontSize: "1rem" } }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          )}
                        </Stack>
                        {onSelectScientificName && (<Tooltip title={t("taxonomy:tree.viewHomonymDetails")} arrow>
                            <Chip
                              clickable
                              onClick={(e) => { e.stopPropagation(); onSelectScientificName(hm); }}
                              icon={<InfoOutlinedIcon fontSize="small" />}
                              label={t("common:open")}
                              sx={{
                                height: 22,
                                borderRadius: 12,
                                color: "aquamarine",
                                border: "1px solid rgba(127,255,212,0.5)",
                                backgroundColor: "rgba(127,255,212,0.08)",
                                ml: "auto",
                                flexShrink: 0,
                                "& .MuiChip-label": { px: 0.8, fontSize: "0.7rem", letterSpacing: 0.3, fontWeight: 700 },
                                "& .MuiChip-icon": { color: "aquamarine", fontSize: "1rem", ml: 0.2 },
                                "&:hover": { backgroundColor: "rgba(127,255,212,0.15)" },
                           }}
                          />
                        </Tooltip> )}
                    </Stack> ); })}
            </Box>
          )}
          {hasInvalidChildren && (
            <Box sx={{ ml: (level + 1) * indentMultiplier, pl: 1, mt: 0.5, mb: 0.5, borderLeft: `2px dashed ${LINE_COLOR}`, py: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mb: 0.5, color: rankStyles.invalid.color }}>
                <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t("taxonomy:tree.invalidGroup")}
                </Typography>
              </Stack>
              <Stack spacing={0.4}>
                {invalidChildren.map((inv) => {
                  const isInvTarget = inv.id === highlightId;
                  const invAuthority = formatAuthority(inv);
                  const invalidTypeLabel = inv.current_invalid_type || t("common:invalid");
                  const invRankLower = (inv.current_rank || "").toLowerCase();
                  const parentRankLower = (node.current_rank || "").toLowerCase();
                  const invItalic = isItalicRank(invRankLower) || isItalicRank(parentRankLower);
                  const invGbifBadge = hasGbifUsageKey(inv);
                  return (
                    <Stack
                      key={inv.id}
                      direction="row"
                      alignItems="center"
                      spacing={0.6}
                      sx={{
                        bgcolor: isInvTarget ? "rgba(239,154,154,0.08)" : "transparent",
                        borderRadius: 1,
                        p: 0.4,
                        flexWrap: "wrap",
                        gap: 0.5,
                        minWidth: 0,
                      }}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        label={invalidTypeLabel}
                        sx={{
                          height: 20,
                          borderColor: "rgba(239,154,154,0.6)",
                          color: rankStyles.invalid.color,
                          "& .MuiChip-label": { px: 0.6, fontSize: "0.72rem", fontWeight: 500 },
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          color: rankStyles.invalid.color,
                          fontStyle: invItalic ? "italic" : "normal",
                          whiteSpace: "nowrap",
                          fontWeight: isInvTarget ? 700 : 500,
                        }}
                      >
                        {inv.name_spell_valid || t("taxonomy:tree.nameNA")}
                      </Typography>
                      {invAuthority.text && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: "lightgray", whiteSpace: "nowrap" }}
                        >
                          {invAuthority.text}
                        </Typography>
                      )}
                      {invGbifBadge && (
                        <Chip
                          size="small"
                          icon={<PublicIcon sx={{ fontSize: "1rem" }} />}
                          label="GBIF"
                          variant="outlined"
                          sx={{
                            height: 22,
                            borderRadius: 12,
                            color: "#7CFFB0",
                            border: "1px solid rgba(124,255,176,0.6)",
                            backgroundColor: "rgba(124,255,176,0.08)",
                            "& .MuiChip-label": { px: 0.6, fontSize: "0.72rem", fontWeight: 600 },
                            "& .MuiChip-icon": { color: "#7CFFB0", fontSize: "1rem", ml: 0.2 },
                          }}
                        />
                      )}
                      {onSelectScientificName && (
                        <Chip
                          clickable
                          onClick={(e) => { e.stopPropagation(); onSelectScientificName(inv); }}
                          icon={<InfoOutlinedIcon fontSize="small" />}
                          label={t("common:open")}
                          sx={{
                            height: 22,
                            borderRadius: 12,
                            color: "aquamarine",
                            border: "1px solid rgba(127,255,212,0.5)",
                            backgroundColor: "rgba(127,255,212,0.08)",
                            ml: "auto",
                            flexShrink: 0,
                            "& .MuiChip-label": { px: 0.8, fontSize: "0.7rem", letterSpacing: 0.3, fontWeight: 700 },
                            "& .MuiChip-icon": { color: "aquamarine", fontSize: "1rem", ml: 0.2 },
                            "&:hover": { backgroundColor: "rgba(127,255,212,0.15)" },
                          }}
                        />
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          )}
          {hasValidChildren && (
            <Box sx={{ ml: (level + 1) * indentMultiplier, pl: 1, mt: 0.4, mb: 0.2, color: "mediumaquamarine" }}>
              <Stack direction="row" alignItems="center" spacing={0.6}>
                <CheckCircleOutlineIcon sx={{ fontSize: "1rem" }} />
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {t("taxonomy:tree.validGroup")}
                </Typography>
              </Stack>
            </Box>
          )}
          {hasValidChildren && validChildren.map((child) => (
            <React.Fragment key={child.id}>
              {renderNode(child, level + 1)}
            </React.Fragment>
          ))}
          {hasMore && (
            <Box sx={{ ml: (level + 1) * (isMobile ? INDENT_MULTIPLIER_MOBILE : INDENT_MULTIPLIER_DESKTOP), py: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  onFetchChildren?.(node.id, nextPage);
                }}
                disabled={loadingChildren.has(node.id)}
              >
                {t("taxonomy:tree.loadMore", { count: totalChildren - loadedCount })}
              </Button>
            </Box>
          )}
        </Collapse>
      </Box>
    );
  // ▼▼▼ 修正点 2: ESLintの警告に従い、不要な依存関係を削除 ▼▼▼
  }, [
    getValidChildren,
    getSpecialChildren,
    expanded,
    highlightId,
    isMobile,
    getAuthorsText,
    getInvalidChildren,
    onSelectScientificName,
    handleNodeClick,
    getDescendantCountByRank,
    fetchedChildren,
    loadingChildren,
    isBotByAudit,
    childrenTotals,
    descendantCountMap,
    formatAuthority,
    formatDescList,
    onFetchChildren,
    persist,
    t,
    formatRankLabel,
    isItalicRank,
    hasGbifUsageKey
  ]);
  // ▲▲▲ 修正点 2 ▲▲▲

  const treeRootNodes = useMemo(() => {
    if (!rootScientificName?.id) return [];
    const rootNode = allScientificNames.find(n => n.id === rootScientificName.id);
    if (!rootNode) return [];
    const parents = getParentChain(rootNode);
    return [...parents, rootNode].filter(Boolean);
  }, [rootScientificName, allScientificNames, getParentChain]);

  return (
    <Box>
      <Stack direction="row" spacing={2} mb={2} justifyContent="center">
        <Button variant="outlined" color="success" onClick={expandAll} startIcon={<ExpandMoreIcon />} sx={{ minWidth: 120 }}>{t("common:expandAll")}</Button>
        <Button variant="outlined" color="error" onClick={collapseAll} startIcon={<ChevronRightIcon />} sx={{ minWidth: 120 }}>{t("common:collapseAll")}</Button>
      </Stack>
      <Divider sx={{ mb: 2, borderColor: "rgba(176, 190, 197, 0.3)" }} />

      {treeRootNodes.length > 0 ? (
        treeRootNodes.map((node) => (
          <React.Fragment key={node?.id ?? `root-node-${Math.random()}`}>
            {renderNode(node, 0)}
          </React.Fragment>
        ))
      ) : (
        <Typography sx={{ textAlign: "center", color: "grey.500", mt: 3 }}>
          {t("taxonomy:tree.selectTaxonPrompt")}
        </Typography>
      )}
    </Box>
  );
}
